import React from 'react';
import { 
  Pencil, 
  Square, 
  Eraser, 
  Undo2, 
  Redo2, 
  Download,
  Ruler,
  PenTool,
  Box,
  Columns,
  Scissors,
  Type,
  DoorOpen,
  MousePointer,
  Wrench,
  Tag,
  Compass,
  Highlighter,
} from 'lucide-react';
import type { ElementType } from '../types';

// Suggestion: Create and import a CSS file for toolbar styles
import './Toolbar.css'; 

interface ToolbarProps {
  currentTool: ElementType;
  onToolSelect: (tool: ElementType) => void;
  onUndo: () => void;
  onRedo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  onDownload: () => void;
  hasTouchScreen: boolean;
  showMeasurements: boolean;
  onFileRepair?: () => void;
  onOpenOrientationModal?: () => void;
  propertyOrientation?: number;
  hideTools?: ElementType[]; // Optional array of tools to hide
}

const Toolbar: React.FC<ToolbarProps> = ({
  currentTool,
  onToolSelect,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  onDownload,
  hasTouchScreen,
  showMeasurements,
  onFileRepair,
  onOpenOrientationModal,
  propertyOrientation,
  hideTools = []
}) => {
  // Create constants for the tool types to ensure type safety
  const TOOLS = {
    SELECT: 'select' as ElementType,
    WALL: 'wall' as ElementType,
    DOOR: 'door' as ElementType,
    ROOM_RESIZE: 'roomResize' as ElementType,
    MEASURE: 'measure' as ElementType,
    ERASER: 'eraser' as ElementType,
    HLP: 'hlp' as ElementType,
    PW: 'pw' as ElementType,
    SW: 'sw' as ElementType,
    SPLIT: 'split' as ElementType,
    TEXT: 'text' as ElementType,
    FILE_REPAIR: 'fileRepair' as ElementType,
    LABEL_ROOM: 'labelRoom' as ElementType,
    HIGHLIGHT: 'highlight' as ElementType,
  };

  return (
    <div className="toolbar-container">
      <div className="toolbar-content">
        {/* History Controls */}
        <div className="tool-group">
          <button
            className={`tool-button ${!canUndo ? 'disabled' : ''}`}
            onClick={onUndo}
            disabled={!canUndo}
            title="Undo"
          >
            <Undo2 size={20} />
          </button>
          <button
            className={`tool-button ${!canRedo ? 'disabled' : ''}`}
            onClick={onRedo}
            disabled={!canRedo}
            title="Redo"
          >
            <Redo2 size={20} />
          </button>
        </div>

        {/* Drawing Tools */}
        <div className="tool-group">
          <button
            className={`tool-button ${currentTool === TOOLS.SELECT ? 'active' : ''}`}
            onClick={() => onToolSelect(TOOLS.SELECT)}
            title="Select Tool"
          >
            <MousePointer size={20} />
          </button>
          <button
            className={`tool-button ${currentTool === TOOLS.WALL ? 'active' : ''}`}
            onClick={() => onToolSelect(TOOLS.WALL)}
            title="Draw Wall"
          >
            <PenTool size={20} />
          </button>
          <button
            className={`tool-button ${currentTool === TOOLS.ROOM_RESIZE ? 'active' : ''}`}
            onClick={() => onToolSelect(TOOLS.ROOM_RESIZE)}
            title="Resize Room & Adjust Corners"
          >
            <Square size={20} />
          </button>
          <button
            className={`tool-button ${currentTool === TOOLS.SPLIT ? 'active' : ''}`}
            onClick={() => onToolSelect(TOOLS.SPLIT)}
            title="Split Wall"
          >
            <Scissors size={20} />
          </button>
          <button
            className={`tool-button ${currentTool === TOOLS.DOOR ? 'active' : ''}`}
            onClick={() => onToolSelect(TOOLS.DOOR)}
            title="Place Door"
          >
            <DoorOpen size={20} />
          </button>
          {!hideTools.includes(TOOLS.HLP) && (
            <button
              className={`tool-button ${currentTool === TOOLS.HLP ? 'active' : ''}`}
              onClick={() => onToolSelect(TOOLS.HLP)}
              title="HLP Wall"
            >
              <Box size={20} className="hlp-icon" />
            </button>
          )}
          {!hideTools.includes(TOOLS.PW) && (
            <button
              className={`tool-button ${currentTool === TOOLS.PW ? 'active' : ''}`}
              onClick={() => onToolSelect(TOOLS.PW)}
              title="PW Wall"
            >
              <Columns size={20} className="pw-icon" />
            </button>
          )}
          {!hideTools.includes(TOOLS.SW) && (
            <button
              className={`tool-button ${currentTool === TOOLS.SW ? 'active' : ''}`}
              onClick={() => onToolSelect(TOOLS.SW)}
              title="SW Wall"
            >
              <Box size={20} className="sw-icon" />
            </button>
          )}
          <button
            className={`tool-button ${currentTool === TOOLS.TEXT ? 'active' : ''}`}
            onClick={() => onToolSelect(TOOLS.TEXT)}
            title="Text Tool"
          >
            <Type size={20} />
          </button>
          <button
            className={`tool-button ${currentTool === TOOLS.LABEL_ROOM ? 'active' : ''}`}
            onClick={() => onToolSelect(TOOLS.LABEL_ROOM)}
            title="Label Room"
          >
            <Tag size={20} />
          </button>
          <button
            className={`tool-button ${showMeasurements ? 'active' : ''}`}
            onClick={() => onToolSelect(TOOLS.MEASURE)}
            title="Toggle Measurements"
          >
            <Ruler size={20} />
          </button>
          <button
            className={`tool-button ${currentTool === TOOLS.ERASER ? 'active' : ''}`}
            onClick={() => onToolSelect(TOOLS.ERASER)}
            title="Eraser"
          >
            <Eraser size={20} />
          </button>
          {!hideTools.includes(TOOLS.HIGHLIGHT) && (
            <button
              className={`tool-button ${currentTool === TOOLS.HIGHLIGHT ? 'active' : ''}`}
              onClick={() => onToolSelect(TOOLS.HIGHLIGHT)}
              title="Highlight"
            >
              <Highlighter size={20} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Toolbar;